import store from "@/store";
import axios from "axios";
import { ConcurrencyManager } from "axios-concurrency";

const MAX_CONCURRENT_REQUESTS_DEFAULT = 2;

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

axios.interceptors.request.use(
  function (config) {
    if (!config.url.includes("countermetrics.org")) {
      // we do not want to send the custom header to countermetrics.org
      // because is messes up the CORS headers
      config.headers["celus-version"] = store.getters.celusVersion;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  function (response) {
    // Clear the version diaolog when no error is returned
    store.dispatch("setNewCelusVersion", { new_version: null });
    // Do something with response data
    return response;
  },
  function (error) {
    // Do something with response error
    if (axios.isCancel(error)) {
      // we ignore this
    } else if (error.response?.status === 401) {
      // if there is 401 error, try to (re)authenticate
      // this can either happen when using shibboleth and the session expires
      // or when using normal login and the session expires
      //
      // in both cases, we only do it if the request was sent to the same origin
      if (
        error.response?.request?.responseURL &&
        error.response?.request?.responseURL.startsWith(window.location.origin)
      ) {
        store.dispatch("cleanUserData");
        store.dispatch("setShowLoginDialog", { show: true });
      }
    } else if (
      error.response?.status === 409 &&
      error.response.headers["celus-version"] !== store.getters.celusVersion
    ) {
      // Display new celus version dialog
      store.dispatch("setNewCelusVersion", {
        new_version: error.response.headers["celus-version"],
      });
    } else if (typeof error.response === "undefined") {
      // this may be any of different errors:
      //  - CORS issue (when accessing the CDN or sentry)
      //  - network issue
      //  - CORS issue caused by EduID session expiration and silent redirect to eduid.cz
      //
      // Because the EduID issue is best handled by properly configuring the server
      // (allowing assets to be accessed without authentication, using 401 in /api responses)
      // we assume EduID does not need to be handled here and just log the error
      console.error(
        "Unknown error (CORS, network, etc.) - ignoring",
        error.config?.url,
      );
    }
    return Promise.reject(error);
  },
);

// install concurrency manager
let max_concurrent_requests = parseInt(
  localStorage.getItem("max_concurrent_requests"),
);
if (!max_concurrent_requests) {
  max_concurrent_requests = MAX_CONCURRENT_REQUESTS_DEFAULT;
}
console.debug("max_concurrent_requests", max_concurrent_requests);
ConcurrencyManager(axios, max_concurrent_requests);

axios.interceptors.request.use(async (config) => {
  // only let requests marked as privileged unless state.letAxiosThrough is true
  // this helps to let requests wait until all the required setup is done (user is logged
  // in, some basic data is loaded, etc.)
  if (
    (store.state.user !== null && store.state.organizations !== null) ||
    config.privileged
  ) {
    return config;
  }
  const watcher = new Promise((resolve) => {
    console.debug(`delaying request for ${config.url}`);
    store.watch(
      (state, getters) => getters.letAxiosThrough,
      (newVal) => {
        if (newVal) resolve();
      },
    );
  });
  try {
    await watcher;
    return config;
  } catch (e) {
    console.error("error waiting for permission to use axios", e);
  }
});

const http_config = async (args) => {
  const { component, group, ...config } = args;

  if (!config["signal"] && component) {
    let grp = group || "";

    // Don’t try to make new requests after cancellation
    if (store.state.cancellation.ongoing[component]) return null;

    if (grp && store.state.cancellation.controllers[component][grp]) {
      // aborting in the same group
      await store.state.cancellation.controllers[component][grp].abort();
    }
    store.dispatch("cancellation/set", { component, group: grp });
    config["signal"] =
      store.state.cancellation.controllers[component][grp].signal;
  }
  return config;
};

const http_error = async (args, e) => {
  const { label, raise, dontShowError, errorTexts, ...config } = args;
  if (axios.isCancel(e)) {
    return { response: null, error: e.message };
  }

  if (raise) {
    throw e;
  }

  let error;
  if (e.response && e.response.data) {
    const { status, data } = e.response;
    if (status === 400) {
      error = data.error;
    }
  }
  if (!dontShowError) {
    if (e?.response?.status && errorTexts?.[e.response.status]) {
      store.dispatch("showError", { message: errorTexts[e.response.status] });
    } else {
      store.dispatch("showError", {
        label,
        error: error || e,
      });
    }
  }
  return { response: null, error: error || e };
};

/**
 * Wrapper for Axios with cancellation per component.
 * @param {string} url - (axios) URL
 * @param {Object} [params] - (axios) GET query parameters
 * @param {string} [method] - (axios) HTTP method "get" by default
 * @param {string} [label] - Description of resource in case of error
 * @param {boolean} [raise] - Throw the exception
 * @param {string} [component] - A unique component identifier for cancellation
 * @param {string} [group] - Mutually exclusive group of requests
 * @returns {Object} - { response, error }
 */
const http = async (args) => {
  const { label, raise, dontShowError, errorTexts, ...config } = args;

  const axios_config = await http_config(args);

  try {
    return {
      response: await axios(axios_config),
      error: null,
    };
  } catch (e) {
    return http_error(args, e);
  }
};

/**
 * Wrapper for Axios with cancellation with http download download per component.
 * @param {string} url - (axios) URL
 * @param {Object} [params] - (axios) GET query parameters
 * @param {string} [method] - (axios) HTTP method "get" by default
 * @param {string} [label] - Description of resource in case of error
 * @param {boolean} [raise] - Throw the exception
 * @param {string} [component] - A unique component identifier for cancellation
 * @param {string} [group] - Mutually exclusive group of requests
 * @returns {Object} - { response, error }
 */
const http_download = async (args) => {
  const { label, raise, dontShowError, errorTexts, ...config } = args;

  let axios_config = await http_config(args);
  axios_config.responseType = "blob";

  try {
    const response = await axios(axios_config);

    // extract filename
    const fileName = response.headers["content-disposition"]
      .split("filename=")[1]
      .replaceAll('"', "");

    // prepare download link and click it
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    // cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      response,
      error: null,
    };
  } catch (e) {
    return http_error(args, e);
  }
};

export { http, http_download };
